import { backbone } from '@visikon/backbone';
import { Translation } from '@visikon/core-models/languageTypes';
import { DeprecatedTokens } from '@visikon/spine/src/styles/tokens/deprecated-tokens';
import { tracker } from '@visikon/tracker/src';
import { useWindowSizeMatches, WindowSize } from '@visikon/utils/src/responsive';
import * as React from 'react';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { ProgramInfo, useContent, useProgramInformationFromContent } from '../../common/selectors';
import { isContentContainer, usePromise } from '../../common/utils';
import { I18N } from '../../internationalization/AppText';
import { IState } from '../../reducers/reducer';
import { MitforlobContent, VideoPlaylist } from '../../reducers/resourcesReducer';
import { useSelfServiceUnsavedChanges } from '../../screens/embeddedMessageScript/EmbeddedMessageScript';
import { ShareProgramSideBar } from '../../screens/sideBar/ShareProgramSideBar';
import { ContentContainerSection } from '../ContentContainerSection';
import { WithFooter } from '../Footer';
import { FlexView, FlexWrapper, HeaderArea, SmallViewSidePadding } from '../styled/ContentAreaContainers';
import { StarButton } from '../styled/StarButton';
import { VideoSection } from './VideoSection';

interface IProps {
  disableFavorites?: boolean;
  isContained?: boolean;
  fieldKey: string;
  videoList?: Translation<VideoPlaylist>;
}

const SectionHeader = styled.header`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const FavoritesContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;

  button {
    margin-top: 0 !important;
    padding-top: 22px;
  }

  @media all and (max-width: ${DeprecatedTokens.breakPoint}) {
    button {
      margin-top: unset !important;
    }
  }
`;

const preserveVideoProps = (apiContent: Translation<VideoPlaylist>, unsavedVideoList: Translation<VideoPlaylist>) => {
  if (!apiContent || !unsavedVideoList) {
    return null;
  }

  const findVideoData = (id: string) => {
    const sections = apiContent.data?.sections || [];
    for (const section of sections) {
      const video = section.videoList.find((v: any) => v.video._id === id);
      if (video) {
        return video.video;
      }
    }
    return null;
  };

  unsavedVideoList?.data?.sections?.map((section: any) => {
    section.videoList.map((video) => {
      video.video = findVideoData(video.video._idRef) || video.video;
    });
  });

  return unsavedVideoList;
};

export const VideoScreenComponent = (props: React.PropsWithChildren<IProps>) => {
  const { disableFavorites, isContained, fieldKey, videoList, children: genericScreenHeader } = props;
  const [showOnlyFavorites, setShowOnlyFavorites] = useState<boolean>(false);
  const contentVideoList = useContent<VideoPlaylist>(fieldKey);
  const myVideoList = videoList || contentVideoList;
  const content = useSelector<IState, MitforlobContent | undefined>((state) => state.resources.content);
  const programInfo = usePromise<ProgramInfo>(useProgramInformationFromContent, content);
  const favorites = useSelector<IState, string[]>((state) => state.userData.favorites);
  const favoritesDisabled = disableFavorites || programInfo?.genericHeader;
  const tabletWindowSize = useWindowSizeMatches(WindowSize.Tablet);
  const languageCode = backbone.store.getState().country.languageCode;

  const unsavedChanges = useSelfServiceUnsavedChanges();
  const operation = unsavedChanges.operations.find((op) => op.contentType == fieldKey);

  const unsavedVideoList = preserveVideoProps(
    myVideoList,
    operation?.data.translations.find((t) => t.language === languageCode),
  );

  const sections = unsavedVideoList
    ? unsavedVideoList.data?.sections ??
      (() => {
        throw new Error('There should be videos?');
      })()
    : myVideoList?.data?.sections || [];

  const toggleFilter = () => {
    const state = showOnlyFavorites ? 'favoriteDisableFilter' : 'favoriteEnableFilter';
    tracker.trackEvent('Favorite', state);
    setShowOnlyFavorites(!showOnlyFavorites);
  };

  if (!sections) {
    return (
      <h1>
        <I18N text="loading_dotdotdot" />
      </h1>
    );
  }

  const shouldDisplay = !isContentContainer();

  return (
    <>
      <FlexWrapper>
        <ContentContainerSection>
          <WithFooter whiteBackground={tabletWindowSize && !isContentContainer} footerStyle={{ paddingLeft: tabletWindowSize ? '16px' : '0' }}>
            <SmallViewSidePadding $shouldBeWhite={true}>
              <HeaderArea $shouldDisplay={shouldDisplay}>
                {favoritesDisabled && genericScreenHeader}
                {!favoritesDisabled && (
                  <div style={{ position: 'relative' }}>
                    <SectionHeader>
                      {genericScreenHeader}
                      <FavoritesContainer>
                        {!disableFavorites && <ShowSaved onClick={toggleFilter} filterFavorites={showOnlyFavorites} />}
                      </FavoritesContainer>
                    </SectionHeader>
                  </div>
                )}
              </HeaderArea>
              <FlexView style={{ marginTop: 8, flexDirection: 'column', minHeight: 'fit-content' }}>
                {sections.map((s, i) => (
                  <VideoSection
                    key={i}
                    length={sections.length}
                    filterByFavorites={showOnlyFavorites}
                    favorites={!favoritesDisabled ? favorites : undefined}
                    section={s}
                    fieldKey={fieldKey}
                    currentPosition={i}
                  />
                ))}
              </FlexView>
            </SmallViewSidePadding>
            {!isContained && tabletWindowSize ? <ShareProgramSideBar /> : null}
          </WithFooter>
        </ContentContainerSection>

        {!isContained && !tabletWindowSize ? <ShareProgramSideBar /> : null}
      </FlexWrapper>
    </>
  );
};

const ShowSaved = ({ filterFavorites, onClick }: { filterFavorites: boolean; onClick: () => void }) => {
  return (
    <StarButton color={DeprecatedTokens.secondary} onClick={onClick}>
      {filterFavorites ? <I18N text="all_videos" /> : <I18N text="favorites" />}
    </StarButton>
  );
};
